// Top-level keyword scanner shared across the structure-extraction modules.
// Walks a byte range at paren-depth 0, skipping strings / comments / quoted
// identifiers via the resolver's tokenSkip helper, and emits one record per
// SQL keyword found at depth 0. Pure function, no tree-sitter.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Query Structure Extractor — SELECT.

import type { ParseOptions } from '../../sql-grammar/model';
import {
  skipContextFor,
  trySkipLeadingTrivia,
  trySkipNonCode,
  type SkipContext,
} from '../classify/tokenSkip';
import { readIdentifier } from '../scope/identifiers';
import { skipParen } from '../scope/aliasScope.scanner';

export interface TopKeyword {
  readonly upper: string;
  readonly start: number;
  readonly end: number;
}

export function scanTopLevelKeywords(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): TopKeyword[] {
  const ctx = skipContextFor(options);
  const out: TopKeyword[] = [];
  let depth = 0;
  let i = start;
  while (i < end) {
    const ident = readIdentifier(source, i, end, options);
    if (ident && (ident.quoted || /[A-Za-z_]/.test(source[i]))) {
      if (!ident.quoted && depth === 0) {
        out.push({ upper: ident.value.toUpperCase(), start: ident.start, end: ident.end });
      }
      i = ident.end;
      continue;
    }
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { depth++; i++; continue; }
    if (c === ')') { if (depth > 0) depth--; i++; continue; }
    i++;
  }
  return out;
}

// Find the byte ranges of each top-level comma at paren-depth 0 in [start, end).
// Returns an array of split offsets; callers pair them with start/end to get
// list-element ranges (e.g. select-list items, GROUP BY items, ORDER BY items).
export function findTopLevelCommas(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): number[] {
  const ctx = skipContextFor(options);
  const out: number[] = [];
  let depth = 0;
  let i = start;
  while (i < end) {
    const ident = readIdentifier(source, i, end, options);
    if (ident && (ident.quoted || /[A-Za-z_]/.test(source[i]))) { i = ident.end; continue; }
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { depth++; i++; continue; }
    if (c === ')') { if (depth > 0) depth--; i++; continue; }
    if (c === ',' && depth === 0) { out.push(i); i++; continue; }
    i++;
  }
  return out;
}

// Locate a top-level keyword by upper-cased value within [start, end). Returns
// the keyword's byte range or null if absent. Used for clause-boundary scans.
export function findTopLevelKeyword(
  keywords: readonly TopKeyword[],
  upper: string,
  fromIndex = 0,
): number {
  for (let k = fromIndex; k < keywords.length; k++) {
    if (keywords[k].upper === upper) return k;
  }
  return -1;
}

// Trim leading/trailing whitespace + comments ONLY from [start, end). Pure
// helper for computing tight ranges around clause content. The leading loop
// deliberately keeps a leading string literal or quoted identifier — at a
// clause-content boundary those are real content (`"c"."country"`, `'lit' AS x`),
// not trivia. It therefore uses `trySkipLeadingTrivia` (comments only) rather
// than `trySkipNonCode` (which skips quoted regions atomically and would eat the
// leading token, corrupting SELECT / GROUP BY / ORDER BY / SET item text).
export function trimRange(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): { startOffset: number; endOffset: number } {
  const ctx = skipContextFor(options);
  let s = start;
  while (s < end) {
    const c = source[s];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { s++; continue; }
    const skipped = trySkipLeadingTrivia(source, s, end, ctx);
    if (skipped > s) { s = skipped; continue; }
    break;
  }
  let e = end;
  while (e > s) {
    const c = source[e - 1];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { e--; continue; }
    break;
  }
  return { startOffset: s, endOffset: e };
}

// End of the code in [start, end) before any trailing comments / whitespace —
// `1 --ar applied` → the offset after `1`. Strings and quoted identifiers are
// code (a `--` inside '…' is not a comment); a comment followed by more code
// (`f(1 /* x */)`) is kept. PostgreSQL and SQL Server nest block comments
// (an inner block comment does not end the outer one). Returns `start` when the range holds no code.
export function codeEndBeforeTrailingComments(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): number {
  const ctx = skipContextFor(options);
  let codeEnd = start;
  let i = start;
  while (i < end) {
    const c = source[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { i++; continue; }
    const comment = nestedBlockCommentEnd(source, i, end, options) ?? trySkipLeadingTrivia(source, i, end, ctx);
    if (comment > i) { i = comment; continue; }
    const quoted = trySkipNonCode(source, i, end, ctx);
    i = quoted > i ? quoted : i + 1;
    codeEnd = i;
  }
  return codeEnd;
}

// End of a nesting block comment starting at `i` (PostgreSQL / SQL Server), else null.
function nestedBlockCommentEnd(source: string, i: number, end: number, options: ParseOptions): number | null {
  if ((options.engine !== 'postgres' && options.engine !== 'mssql') || !source.startsWith('/*', i)) return null;
  let depth = 0;
  for (let j = i; j < end - 1; j++) {
    if (source.startsWith('/*', j)) { depth++; j++; }
    else if (source.startsWith('*/', j)) { depth--; j++; if (depth === 0) return j + 1; }
  }
  return end;
}

// Skip whitespace + comments forward from `start`. Mirrors the cteScope walker.
export function skipWsAndComments(
  source: string,
  start: number,
  end: number,
  ctx: SkipContext,
): number {
  let i = start;
  while (i < end) {
    const c = source[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { i++; continue; }
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped > i) { i = skipped; continue; }
    break;
  }
  return i;
}

// Re-export skipParen so consumers don't need a second import.
export { skipParen };
